import React, { useRef, useState } from "react";
import downloadFile, { openLastDownload, revealLastDownload, shareLastDownload } from "../services/downloadFile";
import { imprimerDocument } from "../services/imprimer";
import { qlAlert } from "../utils/dialogs";
import DocumentViewer from "./DocumentViewer";

const PLATFORM = process.env.REACT_APP_PLATFORM || "web";

// Lien de fichier qui ne fait jamais quitter l'application :
// - downloadOnWeb={false} (« Voir ») ouvre la visionneuse intégrée ;
// - sinon le fichier est téléchargé (blob sur le Web, téléchargeur natif sur
//   Windows et Android) au lieu d'ouvrir un nouvel onglet.
// Tout PDF téléchargeable est aussi imprimable : un bouton « Imprimer » l'accompagne
// (imprimable={false} pour l'enlever).
export default function DownloadLink({
  url,
  filename,
  mimeType,
  authenticated = false,
  openAfterDownload = false,
  downloadOnWeb = true,
  titre,
  children,
  onSuccess,
  imprimable = true,
  target,  // eslint-disable-line no-unused-vars -- ignoré : jamais de nouvel onglet
  rel,     // eslint-disable-line no-unused-vars
  ...anchorProps
}) {
  const [downloaded, setDownloaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apercu, setApercu] = useState(false);
  const downloadLock = useRef(false);

  if (!downloadOnWeb) {
    return (
      <>
        <a href={url} {...anchorProps} onClick={(event) => { event.preventDefault(); setApercu(true); onSuccess?.(); }}>
          {children}
        </a>
        {apercu && (
          <DocumentViewer titre={titre || (filename || "Document").replace(/\.[a-z0-9]+$/i, "")} url={url}
            onClose={() => setApercu(false)} />
        )}
      </>
    );
  }

  const handleClick = async (event) => {
    event.preventDefault();
    if (downloadLock.current) return;
    downloadLock.current = true;
    setLoading(true);
    try {
      const result = await downloadFile({ url, filename, mimeType, authenticated, openAfterDownload });
      if (result?.cancelled) return;
      if (PLATFORM !== "web") setDownloaded(true);
      qlAlert("Fichier téléchargé avec succès.", "success");
      onSuccess?.(result);
    } catch (error) {
      const message = String(error?.message || error || "");
      if (PLATFORM === "web" && !message.includes("session_expired")) {
        // Adresse refusée par le téléchargeur (développement, autre domaine) :
        // la visionneuse intégrée reste disponible, sans quitter l'application.
        setApercu(true);
        return;
      }
      if (message.includes("session_expired")) qlAlert("Votre session a expiré. Reconnectez-vous.", "error");
      else if (message.includes("not_found")) qlAlert("Fichier introuvable.", "error");
      else if (message.includes("domain_refused")) qlAlert("Ce domaine de téléchargement n’est pas autorisé.", "error");
      else if (message.includes("file_exists")) qlAlert("Ce fichier existe déjà. Choisissez un autre nom.", "warning");
      else if (message.includes("file_too_large")) qlAlert("Le fichier est trop volumineux pour être téléchargé.", "error");
      else if (message.includes("invalid_content") || message.includes("empty_file")) qlAlert("Le fichier reçu est vide ou invalide.", "error");
      else if (message.includes("write_error")) qlAlert("Impossible d’enregistrer le fichier à cet emplacement.", "error");
      else if (message.includes("network_error") || message.includes("http_error")) qlAlert("Le téléchargement a échoué. Vérifiez votre connexion puis réessayez.", "error");
      else if (message.includes("open_error")) qlAlert(message.replace(/^.*open_error:\s*/, ""), "warning");
      else qlAlert("Téléchargement impossible.", "error");
    } finally {
      downloadLock.current = false;
      setLoading(false);
    }
  };

  const estPdf = (mimeType || "").includes("pdf") || /\.pdf$/i.test(filename || "");
  // Lien réduit à une icône (bulles de la messagerie) : le bouton « Imprimer » est aussi une icône.
  const iconeSeule = React.Children.toArray(children).every((c) => React.isValidElement(c) && c.type === "i");
  const imprimer = async () => {
    try { await imprimerDocument({ url, filename, mimeType: mimeType || "application/pdf", authenticated }); }
    catch { qlAlert("Impression impossible pour ce fichier.", "error"); }
  };

  const runAction = async (action) => {
    try { await action(); }
    catch { qlAlert("Cette action n’est pas disponible pour ce fichier.", "warning"); }
  };

  return (
    <>
      <a href={url} {...anchorProps} onClick={handleClick} aria-disabled={loading}>
        {loading ? "Téléchargement…" : children}
      </a>
      {imprimable && estPdf && (
        <button type="button" className={`${anchorProps.className || "btn btn-sm btn-outline-secondary"} ql-btn-imprimer${iconeSeule ? "" : " ms-1"}`} onClick={imprimer}
          title="Imprimer" aria-label={`Imprimer ${filename || "le document"}`}>
          {iconeSeule ? <i className="bi bi-printer"></i> : <><i className="bi bi-printer me-1"></i>Imprimer</>}
        </button>
      )}
      {downloaded && (
        <span className="d-inline-flex gap-1 ms-1 align-items-center" role="group" aria-label="Actions du fichier téléchargé">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => runAction(openLastDownload)}>Ouvrir</button>
          {PLATFORM === "windows" && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => runAction(revealLastDownload)}>Dossier</button>}
          {PLATFORM === "android" && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => runAction(shareLastDownload)}>Partager</button>}
          <button type="button" className="btn btn-sm btn-light" onClick={() => setDownloaded(false)} aria-label="Fermer les actions">×</button>
        </span>
      )}
      {apercu && <DocumentViewer titre={titre || filename || "Document"} url={url} onClose={() => setApercu(false)} />}
    </>
  );
}
